'use client'
import Image from "next/image";
import type { Project } from "@/types";
import { useEffect, useState, useRef, useCallback } from "react";
import projectService from "../../../Starglobal3D/utils/services/project";
import Link from "next/link";
import { useLanguage } from "@/contexts/I18nProvider";
import { getLocalizedProjectUrl } from "@/utils/projectUtils";

export default function Carousel({ idField, isScroll: _isScroll, setIsScroll }: { idField: string, isScroll: boolean, setIsScroll: (isScroll: boolean) => void }) {
    const carouselRef = useRef<HTMLDivElement>(null);
    const [projectData, setProjectData] = useState<(Project & { nameTranslate: string })[]>([]);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [imageErrors, setImageErrors] = useState<Record<string, boolean>>({});
    const { locale } = useLanguage();
    const [searchText, setSearchText] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const activeRequestIdRef = useRef(0);

    // Debounce search text
    useEffect(() => {
        const handler = setTimeout(() => {
            if (debouncedSearch !== searchText) {
                setDebouncedSearch(searchText);
                setPage(1);
                setProjectData([]);
                setHasMore(true);
            }
        }, 500);
        return () => clearTimeout(handler);
    }, [searchText, debouncedSearch]);

    // Reset pagination when sector or locale changes
    useEffect(() => {
        setProjectData([]);
        setPage(1);
        setHasMore(true);
        setSearchText("");
        setDebouncedSearch("");
    }, [idField, locale]);

    useEffect(() => {
        const requestId = ++activeRequestIdRef.current;
        const fetchProjectData = async () => {
            if (page > 1 && !hasMore) return;
            setIsLoading(true);
            try {
                const { data } = await projectService.getProjectsByLocation({ 
                    page, 
                    limit: 12, 
                    active: 1, 
                    field: idField, 
                    search: debouncedSearch || undefined,
                    map: '34'
                });

                if (requestId !== activeRequestIdRef.current) return;

                if (data && data.length > 0) {
                    const translatedData = data.map((project: Project & { nameTranslate: string }) => ({
                        ...project,
                        thumbnail: project?.thumbnail,
                        nameTranslate: ""
                    }));
                    
                    setProjectData(prev => page === 1 ? translatedData : [...prev, ...translatedData]);
                    
                    if (data.length < 12) {
                        setHasMore(false);
                    }
                } else {
                    setHasMore(false);
                }
            } catch (error) {
                if (requestId !== activeRequestIdRef.current) return;
                console.error('Error fetching projects:', error);
                setHasMore(false);
            } finally {
                if (requestId === activeRequestIdRef.current) {
                    setIsLoading(false);
                }
            }
        };
        fetchProjectData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idField, locale, page, debouncedSearch]);

    // Intersection Observer for robust cross-browser infinite scroll
    const observer = useRef<IntersectionObserver | null>(null);
    const isLoadingRef = useRef(isLoading);
    const hasMoreRef = useRef(hasMore);

    useEffect(() => {
        isLoadingRef.current = isLoading;
        hasMoreRef.current = hasMore;
    }, [isLoading, hasMore]);

    const bottomRef = useCallback((node: HTMLDivElement | null) => {
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && !isLoadingRef.current && hasMoreRef.current && projectData.length > 0) {
                setPage(prev => prev + 1);
            }
        });
        if (node) observer.current.observe(node);
    }, [projectData.length]);
    return (
        <div className="relative flex flex-col flex-1 w-full h-full pl-4 md:pl-1 pt-1 select-none overflow-visible md:overflow-hidden min-h-0">
            {/* Search Bar */}
            <div className="relative mb-6 shrink-0 mr-4 md:mr-8">
                <svg className="absolute left-4 md:left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-white/70 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                    type="text"
                    placeholder={locale === "en" ? "Search projects..." : locale === "ja" ? "プロジェクトを検索..." : "Tìm kiếm dự án..."}
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    className="w-full bg-[#00000080] text-white placeholder-white/70 pl-11 md:pl-12 pr-11 md:pr-12 py-3 rounded-full outline-none focus:ring-2 focus:ring-[#FFF500] border border-white/30 transition-all font-bold"
                />
                {searchText && (
                    <button 
                        onClick={() => setSearchText("")} 
                        className="absolute right-4 md:right-5 top-1/2 -translate-y-1/2 text-white/70 hover:text-white transition-colors cursor-pointer"
                    >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                )}
            </div>

            <div 
                ref={carouselRef} 
                className="flex-1 w-full overflow-visible md:overflow-hidden md:overflow-y-auto custom-scrollbar min-h-0"
                onScroll={(e) => {
                    if (e.currentTarget.scrollTop >= 1) {
                        setIsScroll(true);
                    } else {
                        setIsScroll(false);
                    }
                }}
            >
            {projectData.length > 0 ? (
                <>
                <div className="relative grid w-full grid-cols-2 gap-x-3 gap-y-6 sm:gap-x-6 sm:gap-y-10 pr-4 md:pr-8 pb-20 md:pb-0 text-center text-white lg:grid-cols-3 sm:grid-cols-2 md:grid-cols-2 h-fit">
                    {projectData.map((project, index) => {
                        const projectId = String(project?.id || index);
                        return (
                        <Link key={projectId} href={getLocalizedProjectUrl(project?.url || project?.URL, locale)} target="_blank" className="block w-full cursor-pointer group">
                            <div className="relative w-full">
                                {project?.thumbnail && !imageErrors[projectId] && project.thumbnail !== "undefined" && project.thumbnail !== "null" && (project.thumbnail.startsWith('http') || project.thumbnail.startsWith('/')) ? (
                                    <Image
                                        src={project.thumbnail}
                                        alt={project.title || project.display_vn || ""}
                                        width={800}
                                        height={450}
                                        unoptimized
                                        onError={() => {
                                            console.error(`Failed to load thumbnail for project ${projectId}: ${project.thumbnail}`);
                                            setImageErrors(prev => ({ ...prev, [projectId]: true }));
                                        }}
                                        className="object-cover w-full aspect-video rounded-lg border-2 md:border-4 border-white pointer-events-none"
                                    />
                                ) : (
                                    <div className="w-full aspect-video bg-black/20 rounded-lg border-2 md:border-4 border-white flex items-center justify-center">
                                        <span className="text-white/50 text-xs sm:text-sm">No Thumbnail</span>
                                    </div>
                                )}
                                <div className="absolute inset-0 bg-black/50 rounded-lg border-2 md:border-4 border-white opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center pointer-events-none">
                                    <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" className="size-10 md:size-12 pointer-events-none">
                                        <g clipPath="url(#clip0_3210_6707)">
                                            <path d="M11.2822 10.7152V7.43017C11.2822 4.11647 13.9685 1.43018 17.2822 1.43018H29.5722C32.8859 1.43018 35.5722 4.11647 35.5722 7.43018V32.5702C35.5722 35.8839 32.8859 38.5702 29.5722 38.5702H17.2822C13.9685 38.5702 11.2822 35.8839 11.2822 32.5702V29.2852" stroke="white" strokeWidth="3" strokeLinecap="round"/>
                                            <path fillRule="evenodd" clipRule="evenodd" d="M17.6875 13.9118C17.6876 13.4555 18.2488 13.2381 18.5566 13.5749L23.9307 19.4528C24.1007 19.639 24.1051 19.9231 23.9414 20.1149L18.5674 26.4127C18.2656 26.7658 17.6875 26.5522 17.6875 26.0876V22.2487H4.92773C4.65173 22.2487 4.42796 22.0246 4.42773 21.7487V17.944C4.42773 17.6679 4.65159 17.444 4.92773 17.444H17.6875V13.9118Z" fill="white"/>
                                        </g>
                                        <defs>
                                            <clipPath id="clip0_3210_6707">
                                                <rect width="40" height="40" fill="white"/>
                                            </clipPath>
                                        </defs>
                                    </svg>
                                </div>
                            </div>
                            <div className="mt-4 md:mt-5 text-left">
                                <h2 className="w-full overflow-hidden text-xs sm:text-sm md:text-base font-medium text-ellipsis whitespace-nowrap text-white/90">
                                    {locale === "en" ? (project?.display_en || project?.title) : locale === "ja" ? (project?.display_ja || project?.display_en || project?.title) : (project?.display_vn || project?.title || project?.display_en)}
                                </h2>
                            </div>
                        </Link>
                    )})}
                </div>
                {/* Sentinel for Infinite Scroll */}
                {hasMore && (
                    <div ref={bottomRef} className="w-full h-10 mt-2 shrink-0 pointer-events-none" />
                )}
                </>
            ) : (
                <div className="w-full text-center text-white mt-10">
                    {locale === "en" ? "No projects found." : "Không có dự án nào."}
                </div>
            )}
            </div>
        </div>
    )
}

